import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Testimonials = () => {
  const testimonials = [
    {
      text: "The session demystified AI for me, offering clear and practical lessons that rebuilt my confidence and prepared me to re-enter the workforce. Riz's personal insights were hugely valuable too.",
      author: "Sarah J, Marketing Agency Owner",
      stars: 5
    },
    {
      text: "I upgraded my skill set through hands-on examples and clear explanations of AI tools that open up new career opportunities.",
      author: "Michael T, Investment Banker",
      stars: 5
    },
    {
      text: "I used to feel overwhelmed by AI, but the program broke down complex concepts into tangible lessons. It boosted my knowledge and confidence without overwhelming jargon",
      author: "Lisa R, Small Business Owner",
      stars: 5
    },
    {
      text: "The course provided a strong foundation in AI concepts that I can apply in real-world settings. The overall curriculum was well-rounded and truly empowering.",
      author: "Nick L, Freelance Consultant",
      stars: 5
    },
    {
      text: "The bespoke session transformed my approach to AI learning. It combined expert guidance with a clear, practical curriculum that has empowered me to upskill and confidently tackle new challenges with an AI first approach.",
      author: "Priya M, Startup Founder",
      stars: 5
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const intervalRef = useRef(null);
  const desktopVisibleTestimonials = 3;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const itemsToShow = isMobile ? 1 : desktopVisibleTestimonials;
  const totalItems = testimonials.length;
  const maxIndexDesktop = totalItems > itemsToShow ? totalItems - itemsToShow : 0;
  const maxIndexMobile = totalItems > 0 ? totalItems - 1 : 0;

  useEffect(() => {
    const canSlide = isMobile ? totalItems > 1 : totalItems > itemsToShow;

    if (!isPaused && canSlide) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prevIndex => {
          const currentMaxIndex = isMobile ? maxIndexMobile : maxIndexDesktop;
          return prevIndex === currentMaxIndex ? 0 : prevIndex + 1;
        });
      }, 5000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, isMobile, itemsToShow, totalItems, maxIndexDesktop, maxIndexMobile]);

  const handlePrevious = () => {
    const canSlide = isMobile ? totalItems > 1 : totalItems > itemsToShow;
    if (!canSlide) return;
    setCurrentIndex(prevIndex => {
      const currentMaxIndex = isMobile ? maxIndexMobile : maxIndexDesktop;
      return prevIndex === 0 ? currentMaxIndex : prevIndex - 1;
    });
  };

  const handleNext = () => {
    const canSlide = isMobile ? totalItems > 1 : totalItems > itemsToShow;
    if (!canSlide) return;
    setCurrentIndex(prevIndex => {
      const currentMaxIndex = isMobile ? maxIndexMobile : maxIndexDesktop;
      return prevIndex === currentMaxIndex ? 0 : prevIndex + 1;
    });
  };

  const goToSlide = (index) => {
    const currentMaxIndex = isMobile ? maxIndexMobile : maxIndexDesktop;
    if (index >= 0 && index <= currentMaxIndex) {
      setCurrentIndex(index);
    }
  };

  const numDots = isMobile ? totalItems : (totalItems > itemsToShow ? maxIndexDesktop + 1 : 1);
  const dotMapArray = Array.from({ length: numDots });

  return (
    <TestimonialsSection id="testimonials">
      <div className="container">
        <motion.h2 
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Success Stories
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <SectionIntro>
            Hear from business leaders who have transformed their operations with our AI coaching.
          </SectionIntro>
        </motion.div>
        
        <CarouselContainer
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <CarouselTrack
            style={{
              transform: isMobile
                ? `translateX(-${currentIndex * 100}%)`
                : `translateX(-${currentIndex * (100 / desktopVisibleTestimonials)}%)`,
              transition: 'transform 0.5s ease',
            }}
          >
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index}>
                <Stars>
                  {[...Array(testimonial.stars)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </Stars>
                <TestimonialText>"{testimonial.text}"</TestimonialText>
                <TestimonialAuthor>- {testimonial.author}</TestimonialAuthor>
              </TestimonialCard>
            ))}
          </CarouselTrack>
          
          {(isMobile ? totalItems > 1 : totalItems > itemsToShow) && (
            <CarouselNavigation>
              <CarouselButton onClick={handlePrevious}>
                <FaChevronLeft />
              </CarouselButton>
              <CarouselDots>
                {dotMapArray.map((_, index) => (
                  <CarouselDot
                    key={index}
                    active={index === currentIndex}
                    onClick={() => goToSlide(index)}
                  />
                ))}
              </CarouselDots>
              <CarouselButton onClick={handleNext}>
                <FaChevronRight />
              </CarouselButton>
            </CarouselNavigation>
          )}
        </CarouselContainer>
      </div>
    </TestimonialsSection>
  );
};

const TestimonialsSection = styled.section`
  background: linear-gradient(90deg, #f8f9fa 0%, #f6f7f8 100%);
  text-align: center;
  padding: 4rem 0;
`;

const SectionIntro = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 3rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
`;

const CarouselTrack = styled.div`
  display: flex;
`;

const TestimonialCard = styled.div`
  background-color: var(--white);
  border-radius: 8px;
  padding: 2rem;
  text-align: left;
  box-shadow: var(--shadow);
  margin: 0 1rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  flex-basis: calc(33.333% - 2rem);

  @media (max-width: 992px) {
    flex-basis: calc(50% - 2rem);
  }

  @media (max-width: 768px) {
    flex-basis: 100%;
    margin: 0;
    padding: 1.5rem;
    scroll-snap-align: start;
  }
`;

const Stars = styled.div`
  color: gold;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 0.25rem;
`;

const TestimonialText = styled.p`
  font-style: italic;
  margin-bottom: 1rem;
  flex-grow: 1;
`;

const TestimonialAuthor = styled.p`
  font-weight: 600;
  margin-bottom: 0;
`;

const CarouselNavigation = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
`;

const CarouselButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background-color: #003b8a;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 1rem;
`;

const CarouselDot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.active ? 'var(--primary-color)' : '#ccc'};
  border: none;
  margin: 0 5px;
  padding: 0;
  cursor: pointer;
  transition: var(--transition);
`;

export default Testimonials;
